import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DOCUMENT } from '../constants/documentTheme';
import { formatInventoryPriceLabel } from '../utils/inventoryPrice';
import { isUsableImageUrl } from '../utils/itineraryItemImage';

export const inventoryDetailLabelSx = {
  fontSize: 10,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
  mb: 0.5,
};

const imageSrc = (item) => item?.imageUrl || item?.image_url || '';

/**
 * Expanded inventory fields: larger photo, price, capacity, description, notes.
 */
const InventoryItemDetails = ({ item, testId }) => {
  const { t } = useTranslation();
  if (!item) return null;

  const src = imageSrc(item);
  const hasImage = isUsableImageUrl(src);
  const description = item.description || '';
  const notes = item.notes || '';
  const capacity = item.capacity;
  const paymentLinkUrl = item.paymentLinkUrl || item.payment_link_url || '';
  const paymentLinkLabel = item.paymentLinkLabel || item.payment_link_label || '';

  return (
    <Box
      data-testid={testId}
      sx={{
        px: { xs: 0, md: 1 },
        pb: 2,
        pt: 0.5,
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: hasImage ? '160px 1fr' : '1fr',
        },
        gap: 2,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      {hasImage ? (
        <Box
          component="img"
          src={src}
          alt=""
          sx={{
            width: '100%',
            maxHeight: 160,
            objectFit: 'cover',
            bgcolor: DOCUMENT.rule,
            display: 'block',
          }}
        />
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        <Box>
          <Typography sx={inventoryDetailLabelSx}>{t('partnerInventory.fieldPrice')}</Typography>
          <Typography sx={{ fontSize: 14 }}>
            {formatInventoryPriceLabel(item, t)}
          </Typography>
        </Box>
        {capacity != null && capacity !== '' ? (
          <Box>
            <Typography sx={inventoryDetailLabelSx}>{t('partnerInventory.fieldCapacity')}</Typography>
            <Typography sx={{ fontSize: 14 }}>
              {t('partnerInventory.capacity', { count: Number(capacity) })}
            </Typography>
          </Box>
        ) : null}
        {description ? (
          <Box>
            <Typography sx={inventoryDetailLabelSx}>{t('partnerInventory.fieldDescription')}</Typography>
            <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap', color: DOCUMENT.ink2 }}>
              {description}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3 }}>
            {t('partnerInventory.noItemDescription')}
          </Typography>
        )}
        {paymentLinkUrl ? (
          <Box>
            <Typography sx={inventoryDetailLabelSx}>{t('partnerInventory.fieldPaymentLink')}</Typography>
            <Link href={paymentLinkUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 14 }}>
              {paymentLinkLabel || paymentLinkUrl}
            </Link>
          </Box>
        ) : null}
        {notes ? (
          <Box>
            <Typography sx={inventoryDetailLabelSx}>{t('partnerInventory.fieldNotes')}</Typography>
            <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', color: DOCUMENT.ink3 }}>
              {notes}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default InventoryItemDetails;
